import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, Loader2, Trash2 } from "lucide-react";
import { adminApi } from "../lib/api";
import { CoursePanel } from "../components/CoursePanel";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const FONT_AR = "'Tajawal','IBM Plex Sans Arabic',-apple-system,system-ui,sans-serif";

const ensureFonts = () => {
  if (document.getElementById("course-font")) return;
  const l = document.createElement("link");
  l.id = "course-font";
  l.rel = "stylesheet";
  l.href = "https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;800;900&display=swap";
  document.head.appendChild(l);
};

export default function PerfAdsAdmin() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetText, setResetText] = useState("");
  const [resetting, setResetting] = useState(false);

  const fetchStats = async () => {
    const token = localStorage.getItem("bb_admin_token");
    if (!token) {
      navigate("/admin/login");
      return;
    }
    try {
      const r = await adminApi().get("/admin/course-stats");
      setStats(r.data);
      setErr(null);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("bb_admin_token");
        navigate("/admin/login");
        return;
      }
      setErr("Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    ensureFonts();
    document.title = "Performance Ads OS — Admin";
    fetchStats();
    const id = setInterval(() => {
      if (document.visibilityState === "visible") fetchStats();
    }, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleReconcile = async () => {
    setReconciling(true);
    try {
      const r = await adminApi().post("/admin/course-reconcile");
      setReconcileResult(r.data);
      toast.success(`Reconciled ${r.data?.summary?.checked || 0} orders · ${r.data?.summary?.fixed || 0} fixed`);
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reconcile failed");
    } finally {
      setReconciling(false);
    }
  };

  const handleReset = async () => {
    if (resetText !== "RESET") {
      toast.error("Type RESET to confirm");
      return;
    }
    setResetting(true);
    try {
      await adminApi().post("/admin/course-reset", { confirm: "RESET" });
      toast.success("Performance Ads OS data wiped");
      setResetOpen(false);
      setResetText("");
      fetchStats();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally {
      setResetting(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("bb_admin_token");
    navigate("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#070C18] text-[#F0EAD9]" style={{ fontFamily: FONT_AR }} data-testid="perfads-admin-root">
      <header className="border-b border-[#C9A66A]/15 bg-[#070C18]/90 backdrop-blur sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-5 py-4 flex items-center justify-between gap-3" dir="ltr">
          <div className="flex items-center gap-3">
            <span className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#C9A66A] to-[#8A6E3D] flex items-center justify-center font-extrabold text-[#0A0F1C] text-[14px]">P</span>
            <div className="leading-tight">
              <div className="font-extrabold text-[15px] tracking-tight text-white">Performance Ads OS — Admin</div>
              <div className="text-[10.5px] text-[#A7956E] tracking-[0.18em]">replyrescue.app/perfads · 99 SAR · LIVE</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              data-testid="perfads-admin-reset-btn"
              onClick={() => setResetOpen(true)}
              className="bg-[#F87171]/10 border-[#F87171]/40 text-[#FCA5A5] hover:bg-[#F87171]/20 hover:text-white"
            >
              <Trash2 className="w-3.5 h-3.5 ml-1.5" /> Reset
            </Button>
            <Button
              variant="outline"
              size="sm"
              data-testid="perfads-admin-logout-btn"
              onClick={logout}
              className="border-[#C9A66A]/35 text-[#E7C98C] hover:bg-[#C9A66A]/15"
            >
              <LogOut className="w-3.5 h-3.5 ml-1.5" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="px-5 py-8 sm:py-10">
        {loading && (
          <div className="max-w-7xl mx-auto py-16 text-center" data-testid="perfads-admin-loading">
            <Loader2 className="w-8 h-8 text-[#C9A66A] animate-spin mx-auto" />
            <div className="mt-4 text-[14px] text-[#A7956E]">Loading stats…</div>
          </div>
        )}
        {!loading && err && (
          <div className="max-w-3xl mx-auto py-16 text-center text-[#FCA5A5]" data-testid="perfads-admin-err">
            {err}
          </div>
        )}
        {!loading && !err && stats && (
          <CoursePanel
            stats={stats}
            onReconcile={handleReconcile}
            reconciling={reconciling}
            reconcileResult={reconcileResult}
          />
        )}
      </main>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent dir="ltr" data-testid="perfads-admin-reset-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Wipe Performance Ads OS data?</AlertDialogTitle>
            <AlertDialogDescription>
              This permanently deletes <strong>all course_orders and course_events</strong> for the Performance Ads OS funnel.
              This cannot be undone. Type <span className="font-mono bg-black/40 px-1.5 py-0.5 rounded">RESET</span> below to confirm.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <Input
            value={resetText}
            onChange={(e) => setResetText(e.target.value)}
            placeholder="Type RESET to confirm"
            data-testid="perfads-admin-reset-input"
            className="font-mono"
          />
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setResetOpen(false); setResetText(""); }} data-testid="perfads-admin-reset-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              disabled={resetText !== "RESET" || resetting}
              data-testid="perfads-admin-reset-confirm"
              className="bg-[#F87171] hover:bg-[#EF4444]"
            >
              {resetting ? "Wiping…" : "Wipe Data"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
